import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton, TextField, Tabs, Tab,
    Menu, MenuItem, Tooltip, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, Alert
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Navbar from "../components/Navbar.js";
import { backendRoot } from "../config";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';

import address1 from '../assets/address1.png';
import address2 from '../assets/address2.png';
import address3 from '../assets/address3.png';
import address4 from '../assets/address4.png';
import address5 from '../assets/address5.png';
import drop1 from '../assets/drop1.png';
import drop2 from '../assets/drop2.png';
import drop3 from '../assets/drop3.png';
import drop4 from '../assets/drop4.png';
import drop5 from '../assets/drop5.png';

// Helper function to format keys into readable text
function formatKey(key) {
    return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function loadGoogleMapsAPI(apiKey) {
    return new Promise((resolve, reject) => {
        if (typeof window !== "undefined" && window.google && window.google.maps) {
            resolve(window.google);
            return;
        }

        const existingScript = document.getElementById("google-maps");
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(window.google));
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.id = "google-maps";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google);
        script.onerror = (err) => reject(err);

        document.body.appendChild(script);
    });
}

function SimpleMap({ lat, lng }) {
    const mapRef = useRef(null);

    useEffect(() => {
        if (!lat || !lng) return;

        loadGoogleMapsAPI(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
            .then(() => {
                if (mapRef.current) {
                    const map = new window.google.maps.Map(mapRef.current, {
                        center: { lat: parseFloat(lat), lng: parseFloat(lng) },
                        zoom: 30,
                        mapTypeId: "satellite",
                    });

                    new window.google.maps.Marker({
                        position: { lat: parseFloat(lat), lng: parseFloat(lng) },
                        map,
                        title: "Location Center",
                    });
                }
            })
            .catch((err) => console.error("Failed to load map:", err));
    }, [lat, lng]);

    return (
        <Box>
            <div ref={mapRef} style={{ height: 400, width: "100%", borderRadius: 8 }} />
        </Box>
    );
}

// Map with just two markers (no directions, no map/satellite buttons)
function MarkersMap({ terminal, house }) {
    const mapRef = useRef(null);

    useEffect(() => {
        if (!terminal?.lat || !terminal?.lng || !house?.lat || !house?.lng) return;

        loadGoogleMapsAPI(process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
            .then(() => {
                const map = new window.google.maps.Map(mapRef.current, {
                    center: { lat: parseFloat(terminal.lat), lng: parseFloat(terminal.lng) },
                    zoom: 18,
                    mapTypeId: "satellite",
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }],
                        },
                    ],
                });

                // Terminal marker (yellow circle)
                new window.google.maps.Marker({
                    position: { lat: parseFloat(terminal.lat), lng: parseFloat(terminal.lng) },
                    map,
                    title: "Terminal",
                    icon: {
                        url:
                            "data:image/svg+xml;utf-8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><circle cx='10' cy='10' r='7' fill='%23FFD600' stroke='%23000' stroke-width='2'/></svg>",
                        scaledSize: new window.google.maps.Size(20, 20),
                    },
                    animation: window.google.maps.Animation.DROP,
                });

                // House marker (red pin)
                new window.google.maps.Marker({
                    position: { lat: parseFloat(house.lat), lng: parseFloat(house.lng) },
                    map,
                    title: "House",
                    icon: {
                        url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                        scaledSize: new window.google.maps.Size(40, 40),
                    },
                    animation: window.google.maps.Animation.DROP,
                });

                // Fit bounds to show both markers
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend({ lat: parseFloat(terminal.lat), lng: parseFloat(terminal.lng) });
                bounds.extend({ lat: parseFloat(house.lat), lng: parseFloat(house.lng) });
                map.fitBounds(bounds, { padding: 50 });

                // Restrict zoom level
                window.google.maps.event.addListenerOnce(map, "bounds_changed", function () {
                    if (map.getZoom() > 19) map.setZoom(19);
                    if (map.getZoom() < 17) map.setZoom(17);
                });
            })
            .catch((err) => console.error("Failed to load map:", err));
    }, [terminal, house]);

    return (
        <Box>
            <div
                ref={mapRef}
                style={{
                    height: 400,
                    width: "100%",
                    borderRadius: 8,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
            />
        </Box>
    );
}


function RightPanel({ selectedRow }) {
    const [tabIndex, setTabIndex] = useState(0);
    const [stableDropData, setStableDropData] = useState(null);
    const [stableAddressData, setStableAddressData] = useState(null);
    const [stableAllData, setStableAllData] = useState(null);

    // Update useEffect to handle data updates more reliably
    useEffect(() => {
        if (selectedRow?.transaction_id) {
            const isValidDrop = selectedRow.temp_drop?.toLowerCase() !== 'na' && selectedRow.temp_drop !== '';

            // Update address data
            if (isValidDrop && selectedRow.building_latitude && selectedRow.building_longitude) {
                setStableAddressData({
                    lat: selectedRow.building_latitude,
                    lng: selectedRow.building_longitude
                });
            } else {
                setStableAddressData(null);
            }

            // Update drop data
            if (isValidDrop && selectedRow.terminal_lat && selectedRow.terminal_lon && selectedRow.building_latitude && selectedRow.building_longitude) {
                setStableDropData({
                    terminal: {
                        lat: selectedRow.terminal_lat,
                        lng: selectedRow.terminal_lon,
                    },
                    house: {
                        lat: selectedRow.building_latitude,
                        lng: selectedRow.building_longitude
                    }
                });
            } else {
                setStableDropData(null);
            }

            // Update all data
            setStableAllData({
                ...selectedRow,
                terminal_coordinates: `${selectedRow.terminal_lat}, ${selectedRow.terminal_lon}`
            });
        }
    }, [selectedRow]); // Depend on the entire selectedRow object to catch all updates

    const handleTabChange = (event, newValue) => setTabIndex(newValue);

    const handleCopyCoordinates = () => {
        if (stableAllData?.terminal_lat && stableAllData?.terminal_lon) {
            const coordinates = `${stableAllData.terminal_lat}, ${stableAllData.terminal_lon}`;
            navigator.clipboard.writeText(coordinates);
        }
    };

    if (selectedRow) {
        return (
            <Box sx={{ width: "40%", display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="View Tabs">
                    <Tab label="Address" />
                    <Tab label="Drop" />
                    {/* <Tab label="Snips" /> */}
                    <Tab label="Comments" />
                    <Tab label="Data" />
                </Tabs>

                <Paper sx={{ flex: 1, p: 2, minHeight: 400, height: 400, display: 'flex', flexDirection: 'column' }}>
                    {tabIndex === 0 && selectedRow?.satellite_image && selectedRow?.temp_drop?.toLowerCase() !== 'null' && (
                        <>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Address Image</Typography>
                            <Box sx={{ height: 340, width: '100%' }}>
                                <img
                                    src={selectedRow.satellite_image}
                                    alt="Address"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, backgroundColor: '#f5f5f5' }}
                                />
                            </Box>
                        </>
                    )}
                    {tabIndex === 1 && selectedRow?.map_image && selectedRow?.temp_drop?.toLowerCase() !== 'null' && (
                        <>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Drop Image</Typography>
                            <Box sx={{ height: 340, width: '100%' }}>
                                <img
                                    src={selectedRow.map_image}
                                    alt="Drop"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, backgroundColor: '#f5f5f5' }}
                                />
                            </Box>
                        </>
                    )}
                    {tabIndex === 2 && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Comments</Typography>
                            {selectedRow?.comment ? (
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    {selectedRow?.temp_drop_change_reason && (
                                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#FF8C00', mb: 0.5 }}>
                                                Reason for Change:
                                            </Typography>
                                            <Typography variant="body1" sx={{ color: '#1a1a1a' }}>
                                                {selectedRow.temp_drop_change_reason}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedRow.comment}
                                    </Typography>
                                    {selectedRow?.comment_updated_by && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Updated by: {selectedRow.comment_updated_by}
                                        </Typography>
                                    )}
                                    {selectedRow?.comment_updated_at && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Updated at: {new Date(selectedRow.comment_updated_at).toLocaleString()}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No comments available for this record.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {/* {tabIndex === 3 && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              {selectedRow.satellite_image ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>Satellite</Typography>
                  <img
                    src={selectedRow.satellite_image}
                    alt="Satellite"
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Box>
              ) : <Typography>No satellite image available</Typography>}
 
              {selectedRow.map_image ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>Uploaded Picture</Typography>
                  <img
                    src={selectedRow.map_image.startsWith("http") ? selectedRow.map_image : `data:image/png;base64,${selectedRow.map_image}`}
                    alt="Uploaded"
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Box>
              ) : <Typography>No uploaded picture available</Typography>}
            </Box>
          )} */}
                    {tabIndex === 3 && stableAllData && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>All Data</Typography>
                            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                {Object.entries(stableAllData)
                                    .filter(([key]) => key !== 'serving_terminal_address')
                                    .map(([key, value]) => {
                                        // Special handling for terminal coordinates
                                        if (key === 'terminal_lat' || key === 'terminal_lon') {
                                            if (key === 'terminal_lat') {
                                                return (
                                                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Typography sx={{ fontSize: '0.95rem' }}>
                                                            <strong style={{ color: '#FF8C00' }}>Terminal Coordinates:</strong> {stableAllData.terminal_coordinates}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleCopyCoordinates}
                                                            sx={{
                                                                color: '#FF8C00',
                                                                '&:hover': { color: '#FF8C00' }
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }

                                        // Handle other_data if it's a string
                                        if (key === 'other_data' && typeof value === 'string') {
                                            try {
                                                const parsedData = JSON.parse(value.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false'));
                                                return Object.entries(parsedData).map(([subKey, subValue]) => (
                                                    <Typography key={`${key}-${subKey}`} sx={{ fontSize: '0.95rem' }}>
                                                        <strong>{formatKey(subKey)}:</strong> {String(subValue)}
                                                    </Typography>
                                                ));
                                            } catch {
                                                return (
                                                    <Typography key={key} sx={{ fontSize: '0.95rem' }}>
                                                        <strong>{formatKey(key)}:</strong> {value}
                                                    </Typography>
                                                );
                                            }
                                        }

                                        // Define the keys that should have yellow headers
                                        const yellowHeaderKeys = [
                                            'source', 'transaction_id', 'full_address', 'address_id',
                                            'complexity', 'building_latitude', 'building_longitude',
                                            'distance_between_term_building', 'water_pond', 'num_of_driveways',
                                            'dense_tree', 'road_in_between', 'terminal_on_road', 'temp_drop_change_reason'
                                        ];

                                        // Handle regular fields
                                        return (
                                            <Typography key={key} sx={{ fontSize: '0.95rem' }}>
                                                <strong style={{
                                                    color: yellowHeaderKeys.includes(key) ? '#FF8C00' : 'inherit',
                                                    fontWeight: 600,
                                                    marginRight: '4px'
                                                }}>
                                                    {formatKey(key)}:
                                                </strong>
                                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                            </Typography>
                                        );
                                    })}
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    } else {
        return null;
    }
}

export default function ProjectDetail() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [projectInfo, setProjectInfo] = useState({});
    const [selectedRow, setSelectedRow] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [tempDropCounts, setTempDropCounts] = useState({ yes: 0, no: 0, na: 0 });
    const [tempDropFilter, setTempDropFilter] = useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;
    const [totalCount, setTotalCount] = useState(0);
    const [addressSearch, setAddressSearch] = useState('');
    const [addressIdSearch, setAddressIdSearch] = useState('');

    // Temp drop edit state variables
    const [showTempDropDialog, setShowTempDropDialog] = useState(false);
    const [newTempDropValue, setNewTempDropValue] = useState('');
    const [comment, setComment] = useState('');
    const [tempDropChangeReason, setTempDropChangeReason] = useState('');
    const [updatingTempDrop, setUpdatingTempDrop] = useState(false);
    const [updateSuccess, setUpdateSuccess] = useState(false);
    const [error, setError] = useState(null);

    // Move isRowSelected function here, before it's used
    const isRowSelected = (row) => {
        const storedTransactionId = localStorage.getItem('selectedTransactionId');
        return row.transaction_id === storedTransactionId;
    };

    useEffect(() => {
        // Check user role from localStorage
        const userDetail = JSON.parse(localStorage.getItem('userDetail'));
        if (userDetail) {
            setIsAdmin(userDetail.is_staff || userDetail.is_superuser);
        }
    }, []);

    useEffect(() => {
        fetchProject(page);
        const interval = setInterval(() => {
            // Only update the records and counts, not the selected row
            fetchRecordsOnly();
        }, 5000);
        return () => clearInterval(interval);
    }, [projectId, page, tempDropFilter, addressSearch, addressIdSearch]);

    useEffect(() => {
        if (page !== 1) {
            setPage(1);
        }
    }, [tempDropFilter, addressSearch, addressIdSearch]);
    // New function to only fetch and update records without affecting selection
    const fetchRecordsOnly = async () => {
        try {
            const records = getHardcodedRecords();
            setRecords(records);
            setTotalCount(records.length);
            setTempDropCounts({ yes: 3, no: 2, na: 0 });
        } catch (error) {
            console.error('Error fetching records:', error);
        }
    };

    const getHardcodedRecords = () => {
        return [
            {
                transaction_id: "20260304133520375981_855257719",
                address_id: "060365712000041",
                full_address: "101 W ALICE ST MOUNT VERNON, MO, 65712",
                placement: "aerial",
                temp_drop: "Y",
                description: "",
                input: 89,
                output: 89,
                source: "TDP Portal",
                terminal_lat: 37.090728770806,
                terminal_lon: -93.819842561939,
                complexity: "Medium",
                building_latitude: 37.0908966064453,
                building_longitude: -93.8192138671875,
                distance_between_term_building: 413.39,
                water_pond: "No",
                num_of_driveways: 0,
                dense_tree: "Yes",
                road_in_between: "No",
                created_datetime: "2026-03-04 18:35",
                satellite_image: address1,
                map_image: drop1,
                order_number: "",
                terminal_number: "",
                created_by: "null",
                message: "",
                direct_distance_between_term_building: 193.3,
                premise_pole_lat: 37.09073919621858,
                premise_pole_lon: -93.8199784453409,
                street_view_image_capture_date: "2014-01",
                terminal_on_road: "No",
                building_streetview_image_link: "https://streetviewpixels-pa.googleapis.com/v1/thumbnail?panoid=2N-doKGaxfF6rsxisfxbRQ&cb_client=search.gws-prod.gps&w=1200&h=800&yaw=189.88137795569426&pitch=0&thumbfov=80",
                remarks: "null",
                nid_location_of_home: "NA",
                nid_device_on_same_side_of_terminal: "NA",
                premise_pole_available: "Yes",
                wiring_available_with_premise_pole: "Yes",
                due_date_interval: 0,
                nearest_premise_pole_distance: 70,
                scenario_name: "Aerial_NoRoadBore_Less150",
                processing_time: "00:02:27",
                comment: "N",
                comment_updated_by: "null",
                comment_updated_at: "2026-03-04T13:35:20.761651-05:00",
                temp_drop_change_reason: "null"
            },
            {
                transaction_id: "20260304133520381025_273610918",
                address_id: "400000007516207",
                full_address: "35 BROADVIEW AVE LUCKEY, OH, 43443",
                placement: "aerial",
                temp_drop: "Y",
                description: "",
                input: 89,
                output: 89,
                source: "TDP Portal",
                terminal_lat: 41.44666206666,
                terminal_lon: -83.488295084454,
                complexity: "Medium",
                building_latitude: 41.446285,
                building_longitude: -83.488747,
                distance_between_term_building: 147.64,
                water_pond: "No",
                num_of_driveways: 0,
                dense_tree: "Yes",
                road_in_between: "Yes",
                created_datetime: "2026-03-04 18:35",
                satellite_image: address2,
                map_image: drop2,
                order_number: "",
                terminal_number: "",
                created_by: "null",
                message: "",
                direct_distance_between_term_building: 185.01,
                premise_pole_lat: 41.446386228481515,
                premise_pole_lon: -83.48873257692155,
                street_view_image_capture_date: "2007-09",
                terminal_on_road: "Yes",
                building_streetview_image_link: "https://streetviewpixels-pa.googleapis.com/v1/thumbnail?panoid=Q8ghzvVWn1dusw6VeJHRdw&cb_client=search.gws-prod.gps&w=1200&h=800&yaw=186.09606514409575&pitch=0&thumbfov=80",
                remarks: "null",
                nid_location_of_home: "NA",
                nid_device_on_same_side_of_terminal: "NA",
                premise_pole_available: "Yes",
                wiring_available_with_premise_pole: "Yes",
                due_date_interval: 0,
                nearest_premise_pole_distance: 11,
                scenario_name: "",
                processing_time: "00:00:35",
                comment: "N",
                comment_updated_by: "null",
                comment_updated_at: "2026-03-04T13:35:20.823849-05:00",
                temp_drop_change_reason: "null"
            },
            {
                transaction_id: "20260304133520382937_543334640",
                address_id: "113445891424742",
                full_address: "12970 SUNSET DR VAN WERT, OH, 45891",
                placement: "aerial",
                temp_drop: "Y",
                description: "",
                input: 89,
                output: 89,
                source: "TDP Portal",
                terminal_lat: 40.846883062503,
                terminal_lon: -84.581494949632,
                complexity: "Medium",
                building_latitude: 40.8470344543457,
                building_longitude: -84.581802368164,
                distance_between_term_building: 262.47,
                water_pond: "No",
                num_of_driveways: 0,
                dense_tree: "Yes",
                road_in_between: "No",
                created_datetime: "2026-03-04 18:35",
                satellite_image: address3,
                map_image: drop3,
                order_number: "",
                terminal_number: "",
                created_by: "null",
                message: "",
                direct_distance_between_term_building: 101.37,
                premise_pole_lat: 40.84703581042874,
                premise_pole_lon: -84.58182870174417,
                street_view_image_capture_date: "2025-08",
                terminal_on_road: "No",
                building_streetview_image_link: "https://streetviewpixels-pa.googleapis.com/v1/thumbnail?panoid=G84qxlFSecRlkJWz-qycCw&cb_client=search.gws-prod.gps&w=1200&h=800&yaw=273.89717769728526&pitch=0&thumbfov=80",
                remarks: "null",
                nid_location_of_home: "NA",
                nid_device_on_same_side_of_terminal: "Yes",
                premise_pole_available: "Yes",
                wiring_available_with_premise_pole: "Yes",
                due_date_interval: 0,
                nearest_premise_pole_distance: 2,
                scenario_name: "Aerial_NoRoadBore_Less150",
                processing_time: "00:01:31",
                comment: "N",
                comment_updated_by: "null",
                comment_updated_at: "2026-03-04T13:35:20.837474-05:00",
                temp_drop_change_reason: "null"
            },
            {
                transaction_id: "20260304133520384322_925182104",
                address_id: "400000008806698",
                full_address: "421 W HARRIS ST DUBLIN, TX, 76446",
                placement: "aerial",
                temp_drop: "N",
                description: "",
                input: 89,
                output: 89,
                source: "TDP Portal",
                terminal_lat: 32.092855042034,
                terminal_lon: -98.344936936762,
                complexity: "Low",
                building_latitude: 32.0924519,
                building_longitude: -98.3449582,
                distance_between_term_building: 141.08,
                water_pond: "No",
                num_of_driveways: 0,
                dense_tree: "No",
                road_in_between: "No",
                created_datetime: "2026-03-04 18:37",
                satellite_image: address4,
                map_image: drop4,
                order_number: "",
                terminal_number: "",
                created_by: "null",
                message: "Google Streetview not available",
                direct_distance_between_term_building: 146.81,
                premise_pole_lat: "null",
                premise_pole_lon: "null",
                street_view_image_capture_date: "",
                terminal_on_road: "Yes",
                building_streetview_image_link: "NA",
                remarks: "null",
                nid_location_of_home: "NA",
                nid_device_on_same_side_of_terminal: "NA",
                premise_pole_available: "NA",
                wiring_available_with_premise_pole: "NA",
                due_date_interval: "Unknown",
                nearest_premise_pole_distance: 0,
                scenario_name: "",
                processing_time: "00:00:46",
                comment: "null",
                comment_updated_by: "null",
                comment_updated_at: "2026-03-04T13:37:06.273035-05:00",
                temp_drop_change_reason: "null"
            },
            {
                transaction_id: "20260304133520388565_719591159",
                address_id: "400000007456999",
                full_address: "5575 DOWLING RD LUCKEY, OH, 43443",
                placement: "aerial",
                temp_drop: "N",
                description: "",
                input: 89,
                output: 89,
                source: "TDP Portal",
                terminal_lat: 41.479002067947,
                terminal_lon: -83.503512960389,
                complexity: "Medium",
                building_latitude: 41.478187,
                building_longitude: -83.5039796,
                distance_between_term_building: 423.23,
                water_pond: "No",
                num_of_driveways: 0,
                dense_tree: "Yes",
                road_in_between: "Yes",
                created_datetime: "2026-03-04 18:38",
                satellite_image: address5,
                map_image: drop5,
                order_number: "",
                terminal_number: "",
                created_by: "null",
                message: "Google Streetview not available",
                direct_distance_between_term_building: 323.35,
                premise_pole_lat: "null",
                premise_pole_lon: "null",
                street_view_image_capture_date: "",
                terminal_on_road: "Yes",
                building_streetview_image_link: "NA",
                remarks: "null",
                nid_location_of_home: "NA",
                nid_device_on_same_side_of_terminal: "NA",
                premise_pole_available: "NA",
                wiring_available_with_premise_pole: "NA",
                due_date_interval: 28,
                nearest_premise_pole_distance: 0,
                scenario_name: "",
                processing_time: "00:00:23",
                comment: "null",
                comment_updated_by: "null",
                comment_updated_at: "2026-03-04T13:38:47.030568-05:00",
                temp_drop_change_reason: "null"
            }
        ];
    };

    const fetchProject = async (pageNumber = 1) => {
        try {
            const records = getHardcodedRecords();

            setRecords(records);
            setTotalCount(records.length);
            setTempDropCounts({ yes: 3, no: 2, na: 0 });

            if (records.length > 0) {
                // On initial load, select the first row
                const firstRow = records[0];
                setSelectedRow(firstRow);
                localStorage.setItem('selectedTransactionId', firstRow.transaction_id);
            }
        } catch (error) {
            console.error('Error fetching project data:', error);
        }
        setLoading(false);
    };

    // Modify handleRowClick to update the selected row and its details
    const handleRowClick = (row) => {
        setSelectedRow(row);
        localStorage.setItem('selectedTransactionId', row.transaction_id);
        setProjectInfo({
            project_name: projectId,
            description: row.description || 'N/A',
            source: row.source || 'N/A',
            created_by: row.created_by || 'N/A',
            start_time: row.created_datetime || 'N/A',
            end_time: row.processing_time || 'N/A',
            progress: (typeof row.output === 'number' &&
                typeof row.input === 'number' &&
                row.input != 0)
                ? Math.round((row.output / row.input) * 100)
                : 0,
        });
    };

    const handleDownload = (type) => {
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        const url = `${backendRoot}/processter/download_tabular_file/${encodeURIComponent(
            projectId
        )}/${type}/`;
        fetch(url, { headers: { Authorization: `Bearer ${authTokens?.access}` } })
            .then((res) => res.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = `${projectId}.${type}`;
                link.click();
            });
    };

    // Temp drop edit functions
    const getAuthToken = () => {
        const authTokens = JSON.parse(localStorage.getItem("authTokens"));
        if (!authTokens?.access) {
            throw new Error("No access token found");
        }
        return authTokens.access;
    };

    const isTokenExpired = (token) => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    };

    const handleAuthError = (error) => {
        console.error("Authentication error:", error);
        localStorage.removeItem("authTokens");
        localStorage.removeItem("userDetail");
        window.location.href = "/login";
    };

    const handleTempDropEdit = (row) => {
        setNewTempDropValue(row.temp_drop || '');
        setComment(''); // Clear comment when opening dialog for edit
        setTempDropChangeReason(''); // Clear reason when opening dialog for edit
        setShowTempDropDialog(true);
    };

    const handleTempDropUpdate = async () => {
        if (!selectedRow || !newTempDropValue) return;

        setUpdatingTempDrop(true);
        const token = getAuthToken();

        try {
            const response = await fetch(`${backendRoot}/processter/update_temp_drop/`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    transaction_id: selectedRow.transaction_id,
                    new_temp_drop: newTempDropValue,
                    comment: comment, // Include comment in the update payload
                    temp_drop_change_reason: tempDropChangeReason // Include reason in the update payload
                }),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || error.error || "Failed to update temp drop value");
            }

            const result = await response.json();

            // Update the local record
            setSelectedRow(prev => ({
                ...prev,
                temp_drop: newTempDropValue,
                comment: comment,
                temp_drop_change_reason: tempDropChangeReason,
                comment_updated_by: result.comment_updated_by,
                comment_updated_at: result.comment_updated_at
            }));

            // Update the record in the records array
            setRecords(prev => prev.map(record =>
                record.transaction_id === selectedRow.transaction_id
                    ? {
                        ...record,
                        temp_drop: newTempDropValue,
                        comment: comment,
                        temp_drop_change_reason: tempDropChangeReason,
                        comment_updated_by: result.comment_updated_by,
                        comment_updated_at: result.comment_updated_at
                    }
                    : record
            ));

            // Refresh data from server to ensure consistency
            setTimeout(() => {
                fetchRecordsOnly();
            }, 1000);

            // Show success message immediately
            setUpdateSuccess(true);

            // Close dialog after a delay
            setTimeout(() => {
                setShowTempDropDialog(false);
                setUpdateSuccess(false);
                setNewTempDropValue('');
                setComment('');
                setTempDropChangeReason(''); // Clear reason on dialog close
            }, 2000); // Close after 2 seconds
        } catch (error) {
            console.error("Error updating temp drop:", error);
            setError(error.message || "Failed to update temp drop value");
        } finally {
            setUpdatingTempDrop(false);
        }
    };

    const handleTempDropDialogClose = () => {
        setShowTempDropDialog(false);
        setNewTempDropValue('');
        setComment('');
        setUpdateSuccess(false);
        setError(null);
        setTempDropChangeReason(''); // Clear reason on dialog close
    };

    // Handle filter dropdown open/close
    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    // Apply filter selection
    const handleFilterSelect = (filterValue) => {
        setTempDropFilter(filterValue);
        handleFilterClose();
    };

    // Clear filter
    const handleClearFilter = () => {
        setTempDropFilter(null);
        handleFilterClose();
    };

    // Add this new function to filter records based on address search
    const getFilteredRecords = () => {
        let filtered = records;

        // Apply temp drop filter if active
        if (tempDropFilter !== null) {
            filtered = filtered.filter(record => {
                const tempDrop = (record.temp_drop || '').toLowerCase().trim();
                switch (tempDropFilter) {
                    case 'yes':
                        return tempDrop === 'y' || tempDrop === 'yes' || tempDrop === 'true';
                    case 'no':
                        return tempDrop === 'n' || tempDrop === 'no' || tempDrop === 'false';
                    case 'na':
                        return !tempDrop || tempDrop === 'na' || tempDrop === 'n/a' || tempDrop === 'null' || tempDrop === 'undefined';
                    default:
                        return true;
                }
            });
        }

        // Apply address search filter if active
        if (addressSearch) {
            const searchTerm = addressSearch.toLowerCase().trim();
            filtered = filtered.filter(record => {
                const address = (record.full_address || '').toLowerCase();
                return address.includes(searchTerm);
            });
        }

        if (addressIdSearch) {
            const searchTerm = addressIdSearch.toLowerCase().trim();
            filtered = filtered.filter(record => {
                const id = (record.address_id || '').toLowerCase();
                return id.includes(searchTerm);
            });
        }

        return filtered;
    };

    // Pagination handler
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Calculate pagination
    const paginatedRecords = getFilteredRecords().slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const totalPages = Math.ceil(totalCount / rowsPerPage);

    // Calculate total pages for filtered results
    const totalFilteredPages = Math.ceil(getFilteredRecords().length / rowsPerPage);

    // Ensure current page doesn't exceed total pages after filtering
    // useEffect(() => {
    //   if (page > totalFilteredPages && totalFilteredPages > 0) {
    //     setPage(totalFilteredPages);
    //   }
    // }, [addressSearch, totalFilteredPages]);

    if (loading)
        return (
            <Box sx={{ background: "#faf9f6", minHeight: "100vh" }}>
                <Navbar />
                <Box sx={{ p: 4 }}>
                    <Typography>Loading...</Typography>
                </Box>
            </Box>
        );

    return (
        <Box sx={{ background: "#faf9f6", minHeight: "100vh" }}>
            <Navbar />
            <Box sx={{ p: 4 }}>
                {/* Back Button */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/app-selector')}
                    sx={{
                        mb: 3,
                        bgcolor: '#42a5f5',
                        color: '#0d1b2a',
                        fontWeight: 600,
                        '&:hover': {
                            bgcolor: '#1e88e5',
                            color: '#fff',
                        },
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                    }}
                >
                    Back
                </Button>

                {/* Project Info Paper */}
                <Paper
                    elevation={2}
                    sx={{
                        p: "12px 24px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        borderRadius: 2,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                        backgroundColor: "#fff",
                        minHeight: 80,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    {/* First Part: Source, Project, Description */}
                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Source:</strong> TDP Portal
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Project:</strong> HYBRIDCheck_2026-03-04_13-34
                        </Typography>
                        <Typography variant="body2">
                            <strong>Description:</strong> N/A
                        </Typography>
                    </Box>

                    {/* Second Part: Created by, Start time, Processing time */}
                    <Box sx={{ flex: 1, minWidth: 250 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Created by:</strong> N/A
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Start time:</strong> 2026-03-04 18:35
                        </Typography>
                        <Typography variant="body2">
                            <strong>Record processing time:</strong> 00:02:27
                        </Typography>
                    </Box>

                    {/* Third Part: Progress Circle and Download Button */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {/* Circular Progress */}
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                            <CircularProgress
                                variant="determinate"
                                value={projectInfo.progress || 0}
                                size={60}
                                thickness={5}
                                sx={{ color: "#1e88e5" }}
                            />
                            <Box
                                sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: "absolute",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{ fontWeight: 600, color: "#1e88e5" }}
                                >
                                    {`${projectInfo.progress || 0}%`}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Download Button */}
                        <Box>
                            {["xlsx"].map((type) => (
                                <IconButton
                                    key={type}
                                    onClick={() => handleDownload(type)}
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: "50%",
                                        color: "#1e88e5",
                                        bgcolor: "#e3f2fd",
                                        m: 0.5,
                                        "&:hover": {
                                            bgcolor: "#bbdefb",
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <CloudDownloadIcon fontSize="medium" />
                                </IconButton>
                            ))}
                        </Box>
                    </Box>
                </Paper>

                {/* Temp Drop Summary */}
                <Paper
                    elevation={2}
                    sx={{
                        p: "10px 24px",
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 2,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                        backgroundColor: "#fff",
                    }}
                >
                    <Typography variant="body1" sx={{ mr: 3, fontWeight: 600 }}>
                        Temp Drop Summary:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                            <Typography>{tempDropCounts.yes}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CancelIcon color="error" sx={{ mr: 1 }} />
                            <Typography>{tempDropCounts.no}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <RemoveCircleOutlineIcon sx={{ color: '#bdbdbd', mr: 1 }} />
                            <Typography>{tempDropCounts.na}</Typography>
                        </Box>
                    </Box>
                </Paper>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mt: 3 }}>
                    <Box
                        sx={{
                            width: !selectedRow ? "100%" : "65%",
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        <TableContainer
                            component={Paper}
                            sx={{
                                overflowX: "hidden",
                                overflowY: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                height: "auto"
                            }}
                        >
                            <Box sx={{
                                flex: 1,
                                minHeight: "auto",
                                overflow: "hidden"
                            }}>
                                <Table
                                    size="small"
                                    sx={{ tableLayout: "fixed" }}
                                >
                                    <TableHead>
                                        <TableRow sx={{ background: "#64b5f6" }}>
                                            {
                                                ["address_id", "full_address", "placement", "temp_drop"].map((key) => {
                                                    const labelMap = {
                                                        address_id: "Address ID",
                                                        full_address: "Address",
                                                        temp_drop: "Temp Drop",
                                                        placement: "Placement"
                                                    };
                                                    const label = labelMap[key] || key;
                                                    return (
                                                        <TableCell
                                                            key={key}
                                                            sx={{
                                                                position: "relative",
                                                                whiteSpace: key === "temp_drop" ? "nowrap" : "normal",
                                                                verticalAlign: "middle",
                                                                height: "40px",
                                                                padding: key === "temp_drop" ? "12px 24px 12px 16px" : undefined,
                                                                width: key === "address_id" ? "200px" :
                                                                    key === "full_address" ? "50%" :
                                                                        key === "placement" ? "15%" : "20%",
                                                                minWidth: key === "address_id" ? "200px" : undefined
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                height: "100%"
                                                            }}>
                                                                {label}
                                                                {key === "temp_drop" && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={handleFilterClick}
                                                                        sx={{
                                                                            ml: 1,
                                                                            mr: 0.5,
                                                                            color: '#1a1a1a',
                                                                            border: '2px solid #1a1a1a',
                                                                            backgroundColor: tempDropFilter ? 'white' : 'rgba(255, 255, 255, 0.6)',
                                                                            '&:hover': {
                                                                                backgroundColor: 'white',
                                                                                boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                                                                            },
                                                                            width: 28,
                                                                            height: 28,
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                        }}
                                                                    >
                                                                        <FilterListIcon fontSize="small" />
                                                                    </IconButton>
                                                                )}
                                                                {key === "full_address" && (
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder="Search..."
                                                                        value={addressSearch}
                                                                        onChange={(e) => setAddressSearch(e.target.value)}
                                                                        sx={{
                                                                            ml: 2,
                                                                            width: '150px',
                                                                            '& .MuiOutlinedInput-root': {
                                                                                height: '32px',
                                                                                backgroundColor: '#e3f2fd',
                                                                                '& fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                            },
                                                                            '& .MuiOutlinedInput-input': {
                                                                                padding: '4px 8px',
                                                                                fontSize: '0.875rem',
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                {key === "address_id" && (
                                                                    <TextField
                                                                        size="small"
                                                                        placeholder="Search..."
                                                                        value={addressIdSearch}
                                                                        onChange={(e) => setAddressIdSearch(e.target.value)}
                                                                        sx={{
                                                                            ml: 2,
                                                                            width: '150px',
                                                                            '& .MuiOutlinedInput-root': {
                                                                                height: '32px',
                                                                                backgroundColor: '#e3f2fd',
                                                                                '& fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                                '&:hover fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                                '&.Mui-focused fieldset': {
                                                                                    borderColor: '#1a1a1a',
                                                                                },
                                                                            },
                                                                            '& .MuiOutlinedInput-input': {
                                                                                padding: '4px 8px',
                                                                                fontSize: '0.875rem',
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Menu
                                                                anchorEl={filterAnchorEl}
                                                                open={Boolean(filterAnchorEl)}
                                                                onClose={handleFilterClose}
                                                            >
                                                                <MenuItem onClick={() => handleFilterSelect('yes')}>
                                                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                                                    <Typography>Yes</Typography>
                                                                </MenuItem>
                                                                <MenuItem onClick={() => handleFilterSelect('no')}>
                                                                    <CancelIcon color="error" sx={{ mr: 1 }} />
                                                                    <Typography>No</Typography>
                                                                </MenuItem>
                                                                <MenuItem onClick={() => handleFilterSelect('na')}>
                                                                    <RemoveCircleOutlineIcon sx={{ color: '#bdbdbd', mr: 1 }} />
                                                                    <Typography>N/A</Typography>
                                                                </MenuItem>
                                                                <MenuItem onClick={handleClearFilter}>
                                                                    <Typography>Clear Filter</Typography>
                                                                </MenuItem>
                                                            </Menu>
                                                        </TableCell>
                                                    );
                                                })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {records.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" style={{ height: 60 }}>
                                                    No records found.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {records.map((row, idx) => (
                                                    <TableRow
                                                        key={row.transaction_id}
                                                        hover
                                                        selected={isRowSelected(row)}
                                                        onClick={() => handleRowClick(row)}
                                                        sx={{
                                                            cursor: "pointer",
                                                            "&.Mui-selected": {
                                                                backgroundColor: "#FFF9C4 !important"
                                                            },
                                                            "&.Mui-selected:hover": {
                                                                backgroundColor: "#FFF176 !important"
                                                            },
                                                            "&:hover": {
                                                                backgroundColor: "#f5f5f5"
                                                            }
                                                        }}
                                                    >
                                                        {["address_id", "full_address", "placement", "temp_drop"].map((key) => (
                                                            <TableCell
                                                                key={key}
                                                                sx={{
                                                                    width: key === "address_id" ? "200px" :
                                                                        key === "full_address" ? "50%" :
                                                                            key === "placement" ? "15%" : "20%",
                                                                    overflow: "hidden",
                                                                    height: 30,
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    textAlign: key === "temp_drop" ? "center" : "left",
                                                                    fontSize: "0.75rem"
                                                                }}
                                                            >
                                                                {key === "temp_drop" ? (
                                                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                                                                        {["y", "yes", "true"].includes(row.temp_drop?.toLowerCase()) ? (
                                                                            <CheckCircleIcon color="success" />
                                                                        ) : ["n", "no", "false"].includes(row.temp_drop?.toLowerCase()) ? (
                                                                            <CancelIcon color="error" />
                                                                        ) : !row.temp_drop || ["na", "n/a"].includes(row.temp_drop?.toLowerCase()) ? (
                                                                            <RemoveCircleOutlineIcon sx={{ color: '#bdbdbd' }} />
                                                                        ) : null}
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleTempDropEdit(row);
                                                                            }}
                                                                            sx={{
                                                                                color: '#FF8C00',
                                                                                '&:hover': { color: '#FF7A00' },
                                                                                width: 20,
                                                                                height: 20
                                                                            }}
                                                                        >
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Box>
                                                                ) : key === "placement" ? (
                                                                    row[key]?.toUpperCase() || ''
                                                                ) : key === "address_id" ? (
                                                                    row[key] || '000000000000000'
                                                                ) : (
                                                                    row[key]
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}

                                                {/* Add empty rows to keep table height constant */}
                                                {Array.from({
                                                    length: rowsPerPage - records.length
                                                }).map((_, idx) => (
                                                    <TableRow key={`empty-row-${idx}`}>
                                                        <TableCell colSpan={4} style={{ height: 30 }} />
                                                    </TableRow>
                                                ))}
                                            </>
                                        )}
                                    </TableBody>
                                </Table>

                                {/* Empty space instead of table rows when fewer than 10 records */}
                                {paginatedRecords.length < 10 && (
                                    <Box sx={{
                                        height: `${(10 - paginatedRecords.length) * 1}px`,
                                        width: "100%"
                                    }} />
                                )}
                            </Box>

                            {/* Spacer before pagination */}
                            <Box sx={{ height: "20px" }} />

                            {/* Pagination controls inside the table container */}
                            <Box sx={{
                                py: 1.5,
                                display: 'flex',
                                justifyContent: 'center',
                                borderTop: '1px solid #e0e0e0'
                            }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handleChangePage}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    size="small"
                                    sx={{
                                        '& .MuiPaginationItem-root.Mui-selected': {
                                            backgroundColor: '#FFD600',
                                            color: '#1a1a1a',
                                            '&:hover': {
                                                backgroundColor: '#FFC400',
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </TableContainer>
                    </Box>

                    <RightPanel selectedRow={selectedRow} />
                </Box>

                {/* Temp Drop Edit Dialog */}
                <Dialog open={showTempDropDialog} onClose={handleTempDropDialogClose} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        Edit Temp Drop Value
                    </DialogTitle>
                    <DialogContent>
                        {updateSuccess ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 48, mb: 2 }} />
                                <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                                    Successfully Updated!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Temp Drop Value: <strong>{newTempDropValue === 'Y' ? 'Yes' : newTempDropValue === 'N' ? 'No' : 'N/A'}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Reason: <strong>{tempDropChangeReason}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Your comment has been saved and will appear in the Comments tab.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Current value: <strong>{selectedRow?.temp_drop || 'N/A'}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    Select a new temp drop value for this record:
                                </Typography>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Temp Drop Value</InputLabel>
                                    <Select
                                        value={newTempDropValue}
                                        onChange={(e) => setNewTempDropValue(e.target.value)}
                                        label="Temp Drop Value"
                                        disabled={updatingTempDrop}
                                    >
                                        <MenuItem value="Y">Yes</MenuItem>
                                        <MenuItem value="N">No</MenuItem>
                                        <MenuItem value="NA">N/A</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Reason of Changing *</InputLabel>
                                    <Select
                                        value={tempDropChangeReason}
                                        onChange={(e) => setTempDropChangeReason(e.target.value)}
                                        label="Reason of Changing *"
                                        disabled={updatingTempDrop}
                                        required
                                    >
                                        <MenuItem value="Company Miss - BSW">Company Miss - BSW</MenuItem>
                                        <MenuItem value="Opened Held Order">Opened Held Order</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Comment"
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    multiline
                                    rows={2}
                                    sx={{ mt: 2 }}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    required
                                    helperText="Please provide a comment for this update"
                                />
                                {error && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {error}
                                    </Alert>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        {updateSuccess ? (
                            <Button
                                onClick={handleTempDropDialogClose}
                                variant="contained"
                                sx={{
                                    bgcolor: "#FF8C00",
                                    color: "#ffffff",
                                    "&:hover": { bgcolor: "#FF7A00" }
                                }}
                            >
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button onClick={handleTempDropDialogClose} disabled={updatingTempDrop}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleTempDropUpdate}
                                    variant="contained"
                                    disabled={updatingTempDrop || !newTempDropValue || !comment || !tempDropChangeReason}
                                    sx={{
                                        bgcolor: "#FF8C00",
                                        color: "#ffffff",
                                        "&:hover": { bgcolor: "#FF7A00" },
                                        "&:disabled": { bgcolor: "#cccccc", color: "#666666" }
                                    }}
                                >
                                    {updatingTempDrop ? (
                                        <>
                                            <CircularProgress size={16} sx={{ mr: 1, color: "#ffffff" }} />
                                            Updating...
                                        </>
                                    ) : "Update"}
                                </Button>
                            </>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}